<template>
    <form-view class="service-map-store-form-view" @submitsuccess="submitSuccess">
        <veui-steps :steps="steps" :current="2"/>
        <template v-if="isEdit">
            <h3>1. 设置字段</h3>
            <h4>添加自定义字段</h4>
            <template slot="field">
                <veui-field label="字段名称：" name="name" field="name" rules="required">
                    <veui-input v-model="store.formData.name"/>
                </veui-field>
                <veui-field label="字段标识：" name="identify" field="identify" rules="required">
                    <veui-input v-model="store.formData.identify"/>
                </veui-field>
                <veui-field label="字段类型" name="type" field="type" rules="required">
                    <veui-select :options="store.fieldTypeDatasource" v-model="store.formData.type"/>
                </veui-field>
            </template>
            <template slot="operation">
                <veui-button ui="primary" type="submit">保存</veui-button>
            </template>
        </template>
        <template slot="after">
            <h4>已添加的自定义字段：</h4>
            <veui-table :data="customFields">
                <veui-table-column field="name" title="字段名称"/>
                <veui-table-column field="identify" title="字段标识"/>
                <veui-table-column field="type" title="字段类型">
                    <template slot-scope="props">{{ store.fieldTypeMap[props.type] }}</template>
                </veui-table-column>
                <veui-table-column v-if="!isDetail" field="opertaions" title="操作">
                    <template slot-scope="props">
                        <veui-button ui="link" @click="deleteCustomField(props.item)">删除</veui-button>
                        <veui-button ui="link" @click="openUpdateDialog(props.item)">修改</veui-button>
                    </template>
                </veui-table-column>
            </veui-table>
            <h4>必填字段：</h4>
            <veui-table :data="store.requiredFieldDescList">
                <veui-table-column field="name" title="字段名称"/>
                <veui-table-column field="identify" title="字段标识"/>
                <veui-table-column field="type" title="字段类型">
                    <template slot-scope="props">{{ store.fieldTypeMap[props.type] }}</template>
                </veui-table-column>
            </veui-table>

            <template v-if="isEdit">
                <h3>2. 上传数据</h3>
                <veui-form>
                    <veui-field label="操作类型：">
                        <veui-select :options="store.operationTypeDatasource" v-model="store.operationType"/>
                        <veui-button @click="download(getDownloadUrl())">下载{{ operationTypeText }}模板</veui-button>
                    </veui-field>
                    <veui-field label="选择文件：">
                        <uploader accept=".csv"
                            name="csv"
                            :action="action"
                            @statuschange="switchBtnStatus"
                            :convertResponse="convertResponse"
                            :max-count="1"
                            :payload="payload">
                            <template slot="desc">
                                <p>上传的数据文件格式为csv；每次上传的文件大小要小于8M。</p>
                                <p>在以新建方式上传门店信息时，会覆盖和之前上传门店地址重复的门店数据，同时，本次所上传门店信息中出现的地址重复的门店数据，第一条生效。</p>
                            </template>
                        </uploader>
                    </veui-field>
                </veui-form>
            </template>


            <div class="operation">
                <veui-button v-if="isEdit && isDraft"
                    v-bind="btnStatus"
                    @click="publish"
                    ui="primary">提交发布</veui-button>
                <veui-button v-else-if="isEdit" @click="goToServiceList">返回服务列表</veui-button>
                <veui-button :class="{'store-btn': isDetail}" @click="back">返回上一步</veui-button>
            </div>

            <veui-dialog title="修改" :open.sync="updateFieldOpen" overlay-class="update-field-dialog">
                <veui-form v-if="updateFieldOpen" ref="updateForm" :data="currentField">
                    <veui-field label="字段名称：" name="name" field="name" rules="required">
                        <veui-input v-model="currentField.name"/>
                    </veui-field>
                    <veui-field label="字段标识：" name="identify" field="identify" rules="required" readonly>
                        <veui-input v-model="currentField.identify"/>
                    </veui-field>
                    <veui-field label="字段类型" name="type" field="type" rules="required" readonly>
                        <veui-select :options="store.fieldTypeDatasource" v-model="currentField.type"/>
                    </veui-field>
                </veui-form>
                <template slot="foot">
                    <veui-button ui="primary small" @click="submitField">保存</veui-button>
                    <veui-button ui="small" @click="updateFieldOpen = false">取消</veui-button>
                </template>
            </veui-dialog>

            <veui-dialog :open.sync="uploadErrorOpen" overlay-class="store-uploader-dialog">
                <store-upload-error :data="currentStoreUploadErrorData"/>
            </veui-dialog>
        </template>
    </form-view>
</template>

<script>
import FormView from '@baidu/ve-ria/views/FormView';
import FormModel from './FormModel';
import createView from '@baidu/ve-ria/views/createView';
import {
    Steps, Field, Button, Input, Span, Select,
    Dialog, Fieldset, RadioGroup, Form, Table, Column
} from 'veui';
import SearchSelect from '@/common/components/SearchSelect';
import StoreUploadError from './StoreUploadError';
import uploaderMixin from '@/common/mixins/uploader';
import serviceMixin from '@/common/mixins/service';
import {safeParse} from '@/common/utils';
import {get, find, filter, includes, extend, isString, isPlainObject} from 'lodash';
import uri from '@baidu/ve-ria/utils/uri';

export default createView(FormView, FormModel, {
    name: 'ServiceMapStoreFormView',
    components: {
        'veui-field': Field,
        'veui-input': Input,
        'veui-span': Span,
        'veui-button': Button,
        'veui-steps': Steps,
        'veui-dialog': Dialog,
        'veui-select': Select,
        'veui-fieldset': Fieldset,
        'veui-radio-group': RadioGroup,
        'veui-table-column': Column,
        'veui-table': Table,
        'veui-form': Form,
        SearchSelect,
        StoreUploadError
    },
    mixins: [serviceMixin, uploaderMixin],
    computed: {
        operationTypeText() {
            return find(this.store.operationTypeDatasource, {value: this.store.operationType}).label;
        },
        action() {
            return this.store.importUrl[this.store.operationType];
        },
        isDraft() {
            return get(this.store.tableDetail, 'serviceStatus') === 'DRAFT';
        },
        payload() {
            return {serviceId: this.store.getRouteParams().serviceId};
        },
        customFields() {
            return filter(this.store.tableDetail.fields, ({identify, status}) => {
                return !includes(['mobile', 'img_url', 'lv_two_indus', 'lv_one_indus'], identify)
                    && !status;
            });
        },
        updateFieldOpen: {
            get() {
                return !!this.currentField;
            },
            set(val) {
                if (!val) {
                    this.currentField = null;
                }
            }
        },
        uploadErrorOpen: {
            get() {
                return !!this.currentStoreUploadErrorData;
            },
            set(val) {
                if (!val) {
                    this.currentStoreUploadErrorData = null;
                }
            }
        }
    },
    data() {
        let steps = [
            {label: '填写基本信息'},
            {label: '上传门店信息'}
        ];
        if (this.isEdit) {
            steps.push({label: '发布服务'});
        }
        return {
            steps,
            currentField: null,
            currentStoreUploadErrorData: null
        };
    },
    methods: {
        getDownloadUrl() {
            return uri.withQuery(
                this.store.excelUrl[this.store.operationType],
                {serviceId: this.store.getRouteParams().serviceId}
            );
        },
        async deleteCustomField({id}) {
            let result = await this.waitConfirm('确定删除？', '温馨提示');
            if (result) {
                this.store.requestDeleteStoreField(id);
                this.showToast('删除成功', {type: 'success'});
                this.store.requestDetail();
            }
        },
        async submitField() {
            let valid = await this.$refs.updateForm.validate();
            if (valid === true) {
                this.updateCustomField();
            }
        },
        async updateCustomField() {
            await this.store.requestUpdateStoreField(this.currentField);
            this.updateFieldOpen = false;
            this.store.requestDetail();
        },
        openUpdateDialog(item) {
            this.currentField = {...item};
        },
        submitSuccess() {
            this.store.formData = {
                name: '',
                identify: '',
                type: null
            };
            this.store.requestDetail();
        },
        async publish() {
            await this.store.requestPublish();
            this.goToServiceList();
        },
        convertResponse(data) {
            // 有些上传的地方依赖了字段url，迁移过来的时候要注意
            let reason = null;
            let {message, success, code, result} = data;
            message = safeParse(message, message);
            success = code === 0 || (String(success) === 'true');
            if (!success && isString(message)) {
                reason = message;
            }
            else if (!success && isPlainObject(message)) {
                const count = Number(message.failedNum);
                const faileCountMsg = count ? `${count}条数据更新失败` : false;
                reason = message.ERROR || faileCountMsg;
            }
            extend(data, {
                status: success ? 'success' : 'failure',
                src: success ? (get(result, 'url') || get(result, 'src')) : '',
                reason: success ? null : reason || '未知错误'
            });
            if (!success) {
                this.currentStoreUploadErrorData = Number(message.failedNum)
                    ? message
                    : null;
            }
            data.result = null;
            return data;
        }
    }
});
</script>

<style lang="less">
.service-map-store-form-view {
    .operation {
        margin-left: 120px;
        .veui-button {
            &+.veui-button {
                margin-left: 20px;
            }
        }
    }

    .store-btn,
    h4 {
        margin-top: 20px;
    }
}
.update-field-dialog {
    .veui-field+.veui-field {
        margin-top: 20px;
    }
}
.store-uploader-dialog {
    div.veui-dialog-content {
        width: 1000px;
    }
}
</style>
